import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Upload, Trash2, Link as LinkIcon, ImageIcon, Pencil, CalendarDays } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ShopBanners = () => {
  const { shopId } = useParams();
  const { api } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [bannerToEdit, setBannerToEdit] = useState(null);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [editLinkUrl, setEditLinkUrl] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await api.get(`/shops/${shopId}/banners`);
      setBanners(res.data);
    } catch {
      toast.error('Erreur lors du chargement des bannières');
    } finally {
      setLoading(false);
    }
  }, [api, shopId]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      if (newLinkUrl.trim()) fd.append('linkUrl', newLinkUrl.trim());
      if (newStartDate) fd.append('startDate', newStartDate);
      if (newEndDate) fd.append('endDate', newEndDate);
      const res = await api.post(`/shops/${shopId}/banners`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBanners(prev => [...prev, res.data]);
      toast.success('Bannière ajoutée');
      setAddDialogOpen(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setNewLinkUrl('');
      setNewStartDate('');
      setNewEndDate('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateLink = async () => {
    if (!bannerToEdit) return;
    try {
      const fd = new FormData();
      fd.append('linkUrl', editLinkUrl.trim());
      fd.append('startDate', editStartDate);
      fd.append('endDate', editEndDate);
      const res = await api.put(`/shops/${shopId}/banners/${bannerToEdit.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBanners(prev => prev.map(b => b.id === bannerToEdit.id ? res.data : b));
      toast.success('Bannière mise à jour');
      setEditDialogOpen(false);
      setBannerToEdit(null);
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!bannerToDelete) return;
    try {
      await api.delete(`/shops/${shopId}/banners/${bannerToDelete.id}`);
      setBanners(prev => prev.filter(b => b.id !== bannerToDelete.id));
      toast.success('Bannière supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
    }
  };

  const canAdd = banners.length < 3;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shop-banners">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Bannières</h1>
          <p className="text-muted-foreground mt-1">
            {banners.length} / 3 bannières
          </p>
        </div>
        <Button
          className="rounded-full"
          onClick={() => setAddDialogOpen(true)}
          disabled={!canAdd}
          data-testid="add-banner-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une bannière
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Les bannières s'affichent en slider sur votre catalogue public. Format recommandé : 1200x400px.
      </p>

      {banners.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ImageIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune bannière</h3>
            <p className="text-muted-foreground mb-6">Ajoutez des bannières promotionnelles à votre catalogue</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {banners.map((banner, index) => (
            <Card key={banner.id} className="overflow-hidden" data-testid={`banner-card-${banner.id}`}>
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-80 h-40 sm:h-auto bg-muted shrink-0">
                  <img
                    src={`${API_BASE}${banner.imageUrl}`}
                    alt={`Bannière ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="flex-1 p-4 flex flex-col justify-between">
                  <div>
                    <p className="font-semibold mb-1">Bannière {index + 1}</p>
                    {banner.linkUrl ? (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate max-w-[300px]">{banner.linkUrl}</span>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Aucun lien</p>
                    )}
                    {(banner.startDate || banner.endDate) ? (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                        <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                        <span>
                          {banner.startDate ? `Du ${banner.startDate}` : 'Début immédiat'}
                          {' — '}
                          {banner.endDate ? `au ${banner.endDate}` : 'sans fin'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                        <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                        <span>Toujours active</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setBannerToEdit(banner);
                        setEditLinkUrl(banner.linkUrl || '');
                        setEditStartDate(banner.startDate || '');
                        setEditEndDate(banner.endDate || '');
                        setEditDialogOpen(true);
                      }}
                      data-testid={`edit-banner-${banner.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        setBannerToDelete(banner);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-banner-${banner.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Banner Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => {
        setAddDialogOpen(open);
        if (!open) { setSelectedFile(null); setPreviewUrl(null); setNewLinkUrl(''); setNewStartDate(''); setNewEndDate(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter une bannière</DialogTitle>
            <DialogDescription>Uploadez une image et ajoutez un lien optionnel</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {previewUrl ? (
              <div className="relative rounded-lg overflow-hidden border">
                <img src={previewUrl} alt="Aperçu" className="w-full h-40 object-cover" />
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                >
                  Changer
                </Button>
              </div>
            ) : (
              <div>
                <input type="file" id="banner-upload" accept="image/*" className="hidden" onChange={handleFileSelect} />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-32 border-dashed"
                  onClick={() => document.getElementById('banner-upload').click()}
                  data-testid="banner-file-input"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-6 h-6 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Cliquez pour sélectionner une image</span>
                    <span className="text-xs text-muted-foreground">1200x400px recommandé</span>
                  </div>
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <Label>Lien (optionnel)</Label>
              <Input
                placeholder="https://... ou /s/ma-boutique/p/mon-produit"
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                data-testid="banner-link-input"
              />
              <p className="text-xs text-muted-foreground">URL vers laquelle la bannière redirige au clic</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  data-testid="banner-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  data-testid="banner-end-date"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Laissez vide pour une bannière toujours active</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleUpload} disabled={!selectedFile || uploading} data-testid="banner-upload-btn">
                {uploading ? 'Envoi...' : 'Ajouter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Link Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier la bannière</DialogTitle>
            <DialogDescription>Modifiez le lien et la programmation</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Lien</Label>
              <Input
                placeholder="https://... ou /s/ma-boutique/p/mon-produit"
                value={editLinkUrl}
                onChange={(e) => setEditLinkUrl(e.target.value)}
                data-testid="banner-edit-link-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                  data-testid="banner-edit-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={editEndDate}
                  onChange={(e) => setEditEndDate(e.target.value)}
                  data-testid="banner-edit-end-date"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Laissez vide pour une bannière toujours active</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdateLink} data-testid="banner-save-link-btn">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la bannière ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShopBanners;
